import React from "react";
import Grid from "@mui/material/Grid";
import { useStoreState } from "app/state/store/hooks";
import { useLocation, useSearchParams } from "react-router-dom";
import { getUrlParamsFromSelectedFilters } from "app/hooks/useUrlFilters";
import { HeaderViewCommonBlockItem } from "app/components/header/sub-components/expanded-view/data";
import { ReactComponent as AIDAExplorerIcon } from "app/assets/vectors/HeaderProducsViewAIDAExplorer.svg";
import { ReactComponent as SectorExplorerIcon } from "app/assets/vectors/HeaderProductViewSectorExplorer.svg";
import { ReactComponent as YourFavouritesIcon } from "app/assets/vectors/HeaderProductViewYourFavourites.svg";
import { ReactComponent as LocationExplorerIcon } from "app/assets/vectors/HeaderProductViewLocationExplorer.svg";
import { ReactComponent as ActivitiesExplorerIcon } from "app/assets/vectors/HeaderProductViewActivitiesExplorer.svg";
import { ReactComponent as OrganisationExplorerIcon } from "app/assets/vectors/HeaderProductViewOrganisationExplorer.svg";
import { useCMSData } from "app/hooks/useCMSData";
import { getCMSDataField } from "app/utils/getCMSDataField";

export const HeaderExplorerView: React.FC = () => {
  const cmsData = useCMSData({ returnData: true });
  const items = [
    {
      title: getCMSDataField(
        cmsData,
        "general.aidaExplorerText",
        "AIDA Explorer"
      ),
      icon: AIDAExplorerIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.aidDataExplorerSubText",
        "See IATI data with visualisations & insights."
      ),
      link: "/explorer",
    },
    {
      title: getCMSDataField(
        cmsData,
        "componentsHeader.locationExplorerText",
        "Location Explorer"
      ),
      icon: LocationExplorerIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.locationExplorerSubText",
        "See Location-Based Insights."
      ),
      link: "/explorer/locations",
    },
    {
      title: getCMSDataField(
        cmsData,
        "componentsHeader.organisationExplorerText",
        "Organisation Explorer"
      ),
      icon: OrganisationExplorerIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.organisationExplorerSubText",
        "See Organisation-Based Insights."
      ),
      link: "/explorer/organisations",
    },
    {
      title: getCMSDataField(
        cmsData,
        "componentsHeader.sectorsExplorerText",
        "Sector Explorer"
      ),
      icon: SectorExplorerIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.sectorsExplorerSubText",
        "See Sector-Based Insights."
      ),
      link: "/explorer/sectors",
    },
    {
      title: getCMSDataField(
        cmsData,
        "componentsHeader.activitiesExplorerText",
        "Activities Explorer"
      ),
      icon: ActivitiesExplorerIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.activitiesExplorerSubText",
        "See and filter activities."
      ),
      link: "/explorer/activities",
    },
    {
      title: getCMSDataField(
        cmsData,
        "componentsHeader.yourFavoritesText",
        "Your Favourites"
      ),
      icon: YourFavouritesIcon,
      subtitle: getCMSDataField(
        cmsData,
        "componentsHeader.yourFavoritesSubText",
        "Save activities, stay in the loop."
      ),
      link: "/explorer/favourites",
    },
  ];
  const location = useLocation();
  const urlParams = useSearchParams()[0];

  const [activeButton, setActiveButton] = React.useState<string>("");

  const selectedFilters = useStoreState((state) => state.appliedFilters);

  const onPathnameChange = (value: string) => {
    switch (value) {
      case items[0].link:
        return items[0].title;
      case items[1].link:
        return items[1].title;
      case items[2].link:
        return items[2].title;
      case items[3].link:
        return items[3].title;
      case items[4].link:
        return items[4].title;
      case items[5].link:
        return items[5].title;
      default:
        return "";
    }
  };

  React.useEffect(() => {
    const newActiveButton = onPathnameChange(location.pathname);
    if (newActiveButton !== activeButton) {
      setActiveButton(newActiveButton);
    }
  }, [location.pathname]);

  const inActivityPage = React.useMemo(
    () => location.pathname.indexOf("/explorer/activities/") > -1,
    [location.pathname]
  );

  const locationSearch = React.useMemo(() => {
    if (location.search) {
      return location.search;
    }
    if (inActivityPage) {
      const queryString = decodeURIComponent(
        getUrlParamsFromSelectedFilters(selectedFilters, urlParams).toString()
      );
      return `${queryString.length > 0 ? "?" : ""}${queryString}`;
    }
    return "";
  }, [location.search, inActivityPage, selectedFilters, urlParams]);

  return (
    <Grid container spacing={2}>
      {items.map((item, i) => (
        <Grid item xs={12} md={12} lg={4} xl={3} key={item.title}>
          <HeaderViewCommonBlockItem
            title={item.title}
            icon={<item.icon />}
            subtitle={item.subtitle}
            active={activeButton === item.title}
            link={item.link + (i < items.length - 1 ? locationSearch : "")}
          />
        </Grid>
      ))}
    </Grid>
  );
};
